import { useState, type Dispatch, type SetStateAction } from 'react';
import { rosterOpenTransition } from '../api/userPaths';

/**
 * State that automatically resets to `closedValue` whenever `isOpen`
 * transitions from true to false — including a parent-driven close that never
 * runs the container's own close handler.
 *
 * Used for state that must not survive a container closing: generated
 * reset/invite links (closedValue `null`) and the "Reset Passwords"
 * confirmation flag (closedValue `false`). The reset happens during render via
 * React's "storing information from previous renders" pattern (not an effect);
 * the transition detection lives in the unit-tested `rosterOpenTransition`.
 */
export function useResetOnClose<T>(
  isOpen: boolean,
  closedValue: T
): readonly [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(closedValue);
  const [wasOpen, setWasOpen] = useState(isOpen);

  const transition = rosterOpenTransition<T>(wasOpen, isOpen, value);
  if (transition.changed) {
    setWasOpen(isOpen);
    if (transition.closed) setValue(closedValue);
  }

  return [value, setValue] as const;
}
